import { useState } from 'react';
import api from '../services/api';

/**
 * @module useSaveMonthlyReport
 * @description Custom hook for saving edited monthly AI reports in the Mental Health Portal application.
 * Manages saving state and provides error handling.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to save monthly AI reports
 * @function useSaveMonthlyReport
 * @returns {Object} Object containing saveMonthlyReport function and isSaving state
 */
export const useSaveMonthlyReport = () => {
  /**
   * @state {boolean} isSaving - Indicates if the save operation is in progress
   */
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Saves an edited monthly AI report
   * @async
   * @function saveReport
   * @param {Object} params - Parameters for saving the report
   * @param {string|number} params.id - ID of the report to save
   * @param {string} params.newAiReport - New AI report content
   * @param {string} params.userId - ID of the user making the edit
   * @returns {Promise<Object>} The saved report data
   * @throws {Error} If the save operation fails
   */
  const saveReport = async ({ id, newAiReport, userId }) => {
    setIsSaving(true);
    try {
      console.log('id, newAiReport, userId', id, newAiReport, userId);
      const { data } = await api.post('update-monthly-ai-report', {
        id,
        newAiReport,
        userId,
      });

      return data;
    } catch (error) {
      throw new Error('Erro ao salvar o relatório mensal');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * @typedef {Object} UseSaveMonthlyReportReturn
   * @property {Function} saveMonthlyReport - Function to save a monthly report
   * @property {boolean} isSaving - Indicates if the save operation is in progress
   */

  /**
   * @returns {UseSaveMonthlyReportReturn} The hook's return values
   */
  return { saveMonthlyReport: saveReport, isSaving };
};
